
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { RequirementDocument } from "@/types/document";
import { toast } from "sonner";

export function useDocumentService() {
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  
  // Fetch documents for a specific requirement
  const fetchDocumentsForRequirement = async (
    requirementId: string,
    projectId: string
  ): Promise<RequirementDocument[]> => {
    try {
      setIsLoading(true);
      
      if (!requirementId || !projectId) {
        console.log("Missing requirementId or projectId - cannot fetch documents");
        return [];
      }
      
      // Get current user ID
      const { data: userData } = await supabase.auth.getUser();
      const currentUserId = userData.user?.id;
      
      if (!currentUserId) {
        console.log("No user ID found, cannot fetch documents");
        return [];
      }
      
      console.log(`Fetching documents for requirement ${requirementId} in project ${projectId} for user ${currentUserId}`);
      
      const { data, error } = await supabase
        .from('requirement_documents')
        .select('*')
        .eq('project_id', projectId)
        .eq('requirement_id', requirementId)
        .eq('user_id', currentUserId)
        .order('uploaded_on', { ascending: false });

      if (error) {
        throw error;
      }

      console.log("Found documents:", data?.length || 0);
      return data || [];
    } catch (error: any) {
      console.error("Error fetching documents:", error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // Download a document
  const downloadDocument = async (doc: RequirementDocument) => {
    try {
      const { data, error } = await supabase.storage
        .from('stdly-project-documents')
        .download(doc.file_path);

      if (error) {
        throw error;
      }

      // Create a download link
      const url = URL.createObjectURL(data);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      window.document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      window.document.body.removeChild(a);
    } catch (error: any) {
      console.error("Error downloading document:", error);
      toast.error("Failed to download document");
    }
  };

  // Delete a document
  const deleteDocument = async (doc: RequirementDocument): Promise<boolean> => {
    try {
      setIsDeleting(doc.id);
      
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('stdly-project-documents')
        .remove([doc.file_path]);

      if (storageError) {
        throw storageError;
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from('requirement_documents')
        .delete()
        .eq('id', doc.id);

      if (dbError) {
        throw dbError;
      }

      toast.success("Document deleted successfully");
      return true;
    } catch (error: any) {
      console.error("Error deleting document:", error);
      toast.error("Failed to delete document");
      return false;
    } finally {
      setIsDeleting(null);
    }
  };

  // Get document counts for multiple requirements
  const fetchDocumentCounts = async (
    requirementIds: string[],
    projectId: string
  ) => {
    try {
      if (!requirementIds.length || !projectId) {
        return [];
      }

      // Get current user ID
      const { data: userData } = await supabase.auth.getUser();
      const currentUserId = userData.user?.id;
      
      if (!currentUserId) {
        console.log("No user ID found, cannot fetch document counts");
        return [];
      }
      
      const { data, error } = await supabase
        .from('requirement_documents')
        .select('requirement_id, project_id')
        .in('requirement_id', requirementIds)
        .eq('project_id', projectId)
        .eq('user_id', currentUserId);

      if (error) {
        throw error;
      }

      // Group by requirement_id and count
      const counts = requirementIds.map(reqId => {
        const docsForReq = data?.filter(d => d.requirement_id === reqId) || [];
        return {
          requirementId: reqId,
          count: docsForReq.length,
          projectId: projectId
        };
      });

      return counts;
    } catch (error: any) {
      console.error("Error fetching document counts:", error);
      return [];
    }
  };

  return {
    isLoading,
    isDeleting,
    fetchDocumentsForRequirement,
    downloadDocument,
    deleteDocument,
    fetchDocumentCounts
  };
}
